import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/components/DataTable";
import { Share } from "lucide-react";
import { User } from "@/__generated__/types";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/Avatar";
import { useShareLink } from "@/hooks/useShareLink";
import { useRoute } from "@/routes/getRoute";

interface TableHeaderProps {
  title: string;
  subtitle: string;
  onFilterChange: (filterId: string, values: string[]) => void;
  filterDisplayValue: string;
  feedPosters: User[];
}

export const TableHeader: React.FC<TableHeaderProps> = ({
  title,
  subtitle,
  onFilterChange,
  filterDisplayValue,
  feedPosters,
}) => {
  const { t } = useTranslation();
  const { shareLink } = useShareLink();
  const getRoute = useRoute();

  const filterOptions = useMemo(
    () => [
      {
        id: "postedBy",
        label: t("feedPost.table.scheduled.filters.postedBy"),
        values: feedPosters.map((poster) => ({
          value: poster.id,
          renderComponent: () => (
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                <AvatarImage
                  src={poster.profilePhoto?.url || undefined}
                  alt={`${poster.firstName} ${poster.lastName || ""}`}
                />
                <AvatarFallback>
                  {(poster.firstName || "U").charAt(0)}
                  {(poster.lastName || "U").charAt(0)}
                </AvatarFallback>
              </Avatar>
              <span>
                {`${poster.firstName} ${poster.lastName || ""}`.trim()}
              </span>
            </div>
          ),
        })),
      },
      {
        id: "ctaEnabled",
        label: t("feedPost.table.scheduled.filters.ctaEnabled"),
        values: [
          {
            value: "yes",
            renderComponent: () => <>{t("global.yes")}</>,
          },
          {
            value: "no",
            renderComponent: () => <>{t("global.no")}</>,
          },
        ],
      },
    ],
    [t, feedPosters],
  );

  // const handleExportToCSV = () => {
  //   toast.success(t("feedPost.table.actions.exportCsv"));
  // };

  return (
    <div className="flex w-full items-start justify-between p-6 pr-0">
      <DataTable.Title title={title} description={subtitle} className="p-0" />
      <div className="flex flex-shrink-0 gap-2">
        <DataTable.Filter
          customFilters={filterOptions}
          onFilterChange={onFilterChange}
          displayValue={filterDisplayValue}
        />
        <DataTable.HeaderActions
          actions={[
            // Hide for now
            // {
            //   key: "export-csv",
            //   render: () => (
            //     <>
            //       <FileDown
            //         className="mr-2 h-4 w-4"
            //         aria-label={t("feedPost.table.actions.exportCsv")}
            //       />
            //       {t("feedPost.table.actions.exportCsv")}
            //     </>
            //   ),
            //   onClick: handleExportToCSV,
            // },
            {
              key: "share",
              render: () => (
                <>
                  <Share
                    className="mr-2 h-4 w-4"
                    aria-label={t("feedPost.table.actions.share")}
                  />
                  {t("feedPost.table.actions.share")}
                </>
              ),
              onClick: () =>
                shareLink(
                  `${getRoute("feedPosts", {}, undefined, true)}#${status}`,
                ),
            },
          ]}
        />
      </div>
    </div>
  );
};
